"use client";

import { useMemo } from "react";
import { usePathname } from "next/navigation";
import {
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ClipboardCheck,
  ClipboardList,
  FilePenLine,
  FlaskConical,
  LogOut,
  Pin,
  Pill,
  Send,
  Settings,
  Timer,
  Undo2,
  User,
} from "lucide-react";

import { NavMain, type SidebarNavSection } from "@/components/nav-main";
import { NavProjects, type QuickViewOption } from "@/components/nav-projects";
import { NavUser } from "@/components/nav-user";
import { TeamSwitcher } from "@/components/team-switcher";
import { HOME_ALERT_COUNT } from "@/components/carehub/facility-non-medical";
import { useRole } from "@/contexts/role-context";
import { useAuth } from "@/contexts/auth-context";
import type { RoleKey } from "@/lib/role-config";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";

const user = {
  name: "Alice Abernathy",
  email: "alice.abernathy@example.org",
  avatar: "https://avatar.vercel.sh/alice.png",
};

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const pathname = usePathname();
  const { roleConfig, roleKey } = useRole();
  const { username } = useAuth();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const projects = useMemo(() => {
    return roleConfig.dashboards.map((dashboard) => {
      const indicator =
        roleKey === "facilityNonMedical" && dashboard.key === "newHome" && HOME_ALERT_COUNT > 0 ? (
          <AlertTriangle className="size-3 text-red-500" />
        ) : undefined;

      return {
        key: dashboard.key,
        name: dashboard.name,
        url: dashboard.path,
        icon: dashboard.icon,
        indicator,
        quickViews: getQuickViewOptions(roleKey, dashboard.key),
      };
    });
  }, [roleConfig.dashboards, roleKey]);

  const sections: SidebarNavSection[] = useMemo(() => {
    return roleConfig.sections.map((section) => {
      const items = section.items.map((item) => {
        const children = item.children?.map((child) => ({
          ...child,
          isActive: pathname === child.path,
        }));
        const isActive = children?.some((child) => child.isActive) || (item.path ? pathname === item.path : false);
        return {
          ...item,
          isActive,
          children,
        };
      });
      return { label: section.label, items };
    });
  }, [pathname, roleConfig.sections]);

  const displayUser = useMemo(() => {
    if (!username) {
      return user;
    }
    return {
      ...user,
      name: username,
    };
  }, [username]);

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="gap-3">
        <TeamSwitcher />
        {isCollapsed ? <SidebarTrigger className="mx-auto mt-2 mb-4" /> : null}
      </SidebarHeader>
      <SidebarContent>
        <NavProjects projects={projects} />
        <NavMain sections={sections} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={displayUser} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

function getQuickViewOptions(_role: RoleKey, dashboardKey: string): QuickViewOption[] {
  switch (dashboardKey) {
    case "newHome":
      return [
        { key: "time-sensitive-actions", label: "Time-Sensitive Actions", icon: Timer },
        { key: "pending-prrs", label: "Pending PRRs", icon: ClipboardList },
        { key: "returned-prrs", label: "Returned PRRs", icon: Undo2 },
        { key: "upcoming-visits-calendar", label: "Upcoming Visits Calendar", icon: CalendarDays },
      ];
    case "commsCenter":
      return [
        { key: "pinned-conversations", label: "Pinned Conversations", icon: Pin },
        { key: "my-conversations", label: "My Conversations", icon: User },
        { key: "my-assigned-actions", label: "My Assigned Actions", icon: ClipboardCheck },
      ];
    case "census":
      return [
        { key: "monthly-schedule", label: "Monthly Schedule", icon: CalendarRange },
        { key: "upcoming-visits", label: "Upcoming Visits", icon: CalendarClock },
        { key: "completed-visits", label: "Completed Visits", icon: ClipboardCheck },
      ];
    case "requests":
      return [
        { key: "draft-requests", label: "Draft Requests", icon: FilePenLine },
        { key: "submitted-requests", label: "Submitted Requests", icon: Send },
        { key: "returned-requests", label: "Returned Requests", icon: Undo2 },
      ];
    case "cases":
      return [
        { key: "recent-med-changes", label: "Recent Med-Changes", icon: Pill },
        { key: "recent-lab-orders", label: "Recent Lab Orders", icon: FlaskConical },
        { key: "recent-discharges", label: "Recent Discharges", icon: LogOut },
        { key: "upcoming-visits", label: "Upcoming Visits", icon: CalendarClock },
      ];
    default:
      return [
        { key: "default-quick-view", label: "Quick View", icon: Settings },
      ];
  }
}
